import { useEffect, useRef } from "react";
import { InfiniteData, useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { queryClient } from "../utils/QueryClient";

import { addStompConnectListener, isStompConnected, subscribeStomp } from "../utils/stompClient";
import { ChatMessageDto, ChatMessageType } from "../models/chat/ChatMessageDto";
import { RootState } from "../stores/Store";
import { chatService } from "../services/ChatService";

export const CHAT_PAGE_SIZE = 30;

const CONVERSATIONS_KEY = ["chat", "conversations"];
const UNREAD_KEY = ["chat", "unread-count"];
const messagesKey = (partnerUserId: string) => ["chat", "messages", partnerUserId];

const invalidateConversations = () => {
  queryClient.invalidateQueries({ queryKey: CONVERSATIONS_KEY });
  queryClient.invalidateQueries({ queryKey: UNREAD_KEY });
};

const appendMessage = (partnerUserId: string, message: ChatMessageDto) => {
  queryClient.setQueryData<InfiniteData<ChatMessageDto[]>>(messagesKey(partnerUserId), (old) => {
    if (!old || old.pages.length === 0) {
      return { pages: [[message]], pageParams: [undefined] };
    }
    if (old.pages.some((page) => page.some((m) => m.id === message.id))) return old;
    return { ...old, pages: [[message, ...old.pages[0]], ...old.pages.slice(1)] };
  });
};

// Thu hồi blob URL (ảnh preview optimistic) để tránh rò rỉ bộ nhớ khi tin tạm bị thay/xoá.
const revokeBlobUrls = (message?: ChatMessageDto) => {
  message?.attachments?.forEach((a) => {
    if (a.url?.startsWith("blob:")) URL.revokeObjectURL(a.url);
  });
};

const findMessage = (partnerUserId: string, id: string): ChatMessageDto | undefined =>
  queryClient
    .getQueryData<InfiniteData<ChatMessageDto[]>>(messagesKey(partnerUserId))
    ?.pages.flat()
    .find((m) => m.id === id);

// Thay tin tạm (optimistic) bằng tin thật từ server; nếu tin thật đã có sẵn (WS về trước) thì bỏ tin tạm.
const reconcileSentMessage = (partnerUserId: string, tempId: string, real: ChatMessageDto) => {
  revokeBlobUrls(findMessage(partnerUserId, tempId));
  queryClient.setQueryData<InfiniteData<ChatMessageDto[]>>(messagesKey(partnerUserId), (old) => {
    if (!old) return old;
    const realExists = old.pages.some((page) => page.some((m) => m.id === real.id));
    return {
      ...old,
      pages: old.pages.map((page) => page.flatMap((m) => (m.id !== tempId ? [m] : realExists ? [] : [{ ...real, status: undefined }]))),
    };
  });
};

const updateMessageStatus = (partnerUserId: string, id: string, status: ChatMessageDto["status"]) => {
  queryClient.setQueryData<InfiniteData<ChatMessageDto[]>>(messagesKey(partnerUserId), (old) => {
    if (!old) return old;
    return {
      ...old,
      pages: old.pages.map((page) => page.map((m) => (m.id === id ? { ...m, status } : m))),
    };
  });
};

export const removeChatMessage = (partnerUserId: string, id: string) => {
  revokeBlobUrls(findMessage(partnerUserId, id));
  queryClient.setQueryData<InfiniteData<ChatMessageDto[]>>(messagesKey(partnerUserId), (old) => {
    if (!old) return old;
    return { ...old, pages: old.pages.map((page) => page.filter((m) => m.id !== id)) };
  });
};

export const makeTempMessageId = (): string =>
  "temp-" + (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

export const useChatConversations = (enabled = true) =>
  useQuery({ queryKey: CONVERSATIONS_KEY, queryFn: chatService.getChatConversations, enabled, staleTime: 15_000 });

export const useChatUnreadCount = (enabled = true) =>
  useQuery({ queryKey: UNREAD_KEY, queryFn: chatService.getChatUnreadCount, enabled, staleTime: 30_000 });

// Trang đầu = tin mới nhất
export const useChatMessages = (partnerUserId: string, enabled = true) =>
  useInfiniteQuery({
    queryKey: messagesKey(partnerUserId),
    queryFn: ({ pageParam }) => chatService.getChatMessages(partnerUserId, pageParam, CHAT_PAGE_SIZE),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => (lastPage.length === CHAT_PAGE_SIZE ? lastPage[lastPage.length - 1].id : undefined),
    enabled,
    staleTime: Infinity, // cache cập nhật qua WS/append, không cần refetch nền
  });

interface SendChatVars {
  recipientUserId: string;
  content: string;
  messageType?: ChatMessageType;
  tempId: string;
}

export const useSendChatMessage = () => {
  const myUserId = useSelector((state: RootState) => state.auth.userId);
  return useMutation({
    mutationFn: ({ recipientUserId, content, messageType = "TEXT" }: SendChatVars) =>
      chatService.sendChatMessage(recipientUserId, content, messageType),
    // Optimistic: hiện tin ngay với trạng thái "sending" để mạng chậm không bị "biến mất".
    onMutate: ({ recipientUserId, content, messageType = "TEXT", tempId }) => {
      appendMessage(recipientUserId, {
        id: tempId,
        senderUserId: myUserId ?? "",
        recipientUserId,
        content,
        messageType,
        isRead: false,
        registeredTime: "",
        status: "sending",
      });
    },
    onSuccess: (message, { recipientUserId, tempId }) => {
      reconcileSentMessage(recipientUserId, tempId, message);
      invalidateConversations();
    },
    onError: (_err, { recipientUserId, tempId }) => {
      updateMessageStatus(recipientUserId, tempId, "failed");
    },
  });
};

interface SendAttachmentVars {
  recipientUserId: string;
  files: File[];
  content?: string;
  tempId: string;
}

export const useSendChatAttachment = () => {
  const myUserId = useSelector((state: RootState) => state.auth.userId);
  return useMutation({
    mutationFn: ({ recipientUserId, files, content }: SendAttachmentVars) => chatService.sendChatAttachment(recipientUserId, files, content),
    // Optimistic: hiện tin ngay; ảnh preview bằng object URL local trong lúc upload.
    onMutate: ({ recipientUserId, files, content, tempId }) => {
      const attachments = files.map((f, i) => {
        const isImage = f.type.startsWith("image/");
        return {
          id: `${tempId}-${i}`,
          originalFileName: f.name,
          fileSize: f.size,
          contentType: f.type,
          isImage,
          url: isImage ? URL.createObjectURL(f) : null,
        };
      });
      appendMessage(recipientUserId, {
        id: tempId,
        senderUserId: myUserId ?? "",
        recipientUserId,
        content: content ?? "",
        messageType: "ATTACHMENT",
        isRead: false,
        registeredTime: "",
        attachments,
        status: "sending",
      });
    },
    onSuccess: (message, { recipientUserId, tempId }) => {
      reconcileSentMessage(recipientUserId, tempId, message);
      invalidateConversations();
    },
    onError: (_err, { recipientUserId, tempId }) => {
      updateMessageStatus(recipientUserId, tempId, "failed");
    },
  });
};

export const useMarkChatRead = () =>
  useMutation({
    mutationFn: (partnerUserId: string) => chatService.markChatConversationRead(partnerUserId),
    onSuccess: invalidateConversations,
  });

// Nhận tin
export const useChatSocket = (enabled: boolean, onIncoming?: (message: ChatMessageDto) => void) => {
  const myUserId = useSelector((state: RootState) => state.auth.userId);
  const onIncomingRef = useRef(onIncoming);
  onIncomingRef.current = onIncoming;

  useEffect(() => {
    if (!enabled || !myUserId) return;
    // skip connect đầu
    let skipNextConnect = !isStompConnected();
    const offConnect = addStompConnectListener(() => {
      if (skipNextConnect) {
        skipNextConnect = false;
        return;
      }
      queryClient.invalidateQueries({ queryKey: ["chat"] });
    });
    const offMessage = subscribeStomp<ChatMessageDto>("/user/queue/chat", (message) => {
      const partnerUserId = message.senderUserId === myUserId ? message.recipientUserId : message.senderUserId;
      appendMessage(partnerUserId, message);
      invalidateConversations();
      if (message.senderUserId !== myUserId) onIncomingRef.current?.(message);
    });
    return () => {
      offConnect();
      offMessage();
    };
  }, [enabled, myUserId]);
};
